import { useState, useEffect, useRef } from "react";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { Modal as AriaModal, ModalOverlay } from "react-aria-components";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import http from "../common/axios";
import { Check, X, Scan } from "lucide-react";
import useRedeemCoupon from "@/hooks/useRedeemCoupon";
import { captureException } from "@sentry/react";
import { trackEvent } from "@/lib/amplitude";

interface CouponVerificationResponse {
  isValid: boolean;
  coupon?: {
    restaurant_id: number;
    expiresAt: string;
    isRedeemed: boolean;
    discount_amount?: number;
  };
  reason?: string;
}

function ScanQRCode() {
  const { t, i18n } = useTranslation();
  const [scannedData, setScannedData] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] =
    useState<CouponVerificationResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const placeRef = localStorage.getItem("placeRef");
  const hasTrackedPageView = useRef(false);

  // Track page view
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackEvent("coupon_scan_started", { placeRef });
    }
  }, []);

  // Map i18n language codes to date locale codes
  const getDateLocale = () => {
    switch (i18n.language) {
      case "en":
        return "en-US";
      case "de":
        return "de-DE";
      case "it":
      default:
        return "it-IT";
    }
  };

  const { mutate: redeemCoupon, isPending: isRedeeming } = useRedeemCoupon({
    onSuccess: (data) => {
      if (data.isRedeemed) {
        trackEvent("coupon_redeemed", { placeRef, couponCode: scannedData });
        setRedeemed(true);
      } else {
        setVerificationResult({
          isValid: false,
          reason: data.message || "Failed to redeem coupon.",
        });
      }
    },
    onError: (error) => {
      console.error("Error redeeming coupon:", error);
      captureException(error);
      trackEvent("coupon_redeem_error", {
        placeRef,
        error: error instanceof Error ? error.message : "Unknown error",
      });
      setVerificationResult({
        isValid: false,
        reason: "Failed to redeem coupon. Please try again.",
      });
    },
  });

  const handleRedeemCoupon = () => {
    if (!scannedData || !placeRef) return;

    trackEvent("coupon_redemption_confirmed", { placeRef, couponCode: scannedData });
    redeemCoupon({
      code: scannedData,
      place_id: parseInt(placeRef),
    });
  };

  const verifyCoupon = async (code: string) => {
    setIsVerifying(true);
    setIsModalOpen(true);
    setVerificationResult(null);

    try {
      const response = await http.post<CouponVerificationResponse>(
        `/places/coupon/verify`,
        { code, place_id: placeRef ? parseInt(placeRef) : null },
      );
      setVerificationResult(response.data);
      trackEvent("coupon_verified", {
        placeRef,
        couponCode: code,
        isValid: response.data.isValid,
      });
    } catch (err) {
      console.error("Error verifying coupon:", err);
      captureException(err);
      trackEvent("coupon_verify_error", {
        placeRef,
        couponCode: code,
        error: err instanceof Error ? err.message : "Unknown error",
      });
      setVerificationResult({
        isValid: false,
        reason: "Failed to verify coupon. Please try again.",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUpdate = (_err: unknown, result: { text: string } | null) => {
    if (result) {
      const qrCode = result.text;
      setScannedData(qrCode);
      setError("");
      trackEvent("coupon_scanned", { placeRef, qrCode });

      // Extract coupon code and verify
      const couponCodeMatch = qrCode.match(/([a-f0-9-]+)/i);
      if (couponCodeMatch) {
        verifyCoupon(couponCodeMatch[0]);
      } else {
        setError(t("coupon.error.invalidFormat"));
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setScannedData("");
    setVerificationResult(null);
    setRedeemed(false);
  };

  return (
    <div className="fixed inset-0 flex h-full w-full flex-col bg-background">
      {/* Header */}
      <div className="absolute left-0 right-0 top-0 z-10 space-y-2 bg-gradient-to-b from-background via-background/95 to-transparent p-4 pb-24">
        {/* Language Switcher */}
        <button
          onClick={() =>
            i18next.changeLanguage(
              i18next.language === "en" ? "it" : i18next.language === "it" ? "de" : "en",
            )
          }
          className="ml-auto block rounded-lg bg-medium-gray px-3 py-1.5 text-xs font-semibold text-foreground transition-all hover:bg-light-gray active:scale-95"
        >
          {i18next.language === "en" ? "ENG" : i18next.language === "it" ? "ITA" : "DEU"}
        </button>

        <h1 className="text-center text-2xl font-bold text-foreground">
          {t("coupon.scan.title")}
        </h1>
        <p className="mt-2 text-center text-sm text-brand-gray">
          {t("coupon.scan.subtitle")}
        </p>
      </div>

      {/* Scanner Area */}
      {!isModalOpen && (
        <div className="relative flex h-full w-full items-center justify-center">
          <BarcodeScannerComponent width="100%" height="100%" onUpdate={handleUpdate} />

          {/* Scanner Frame Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative h-64 w-64">
              {/* Corner borders */}
              <div className="absolute left-0 top-0 h-12 w-12 rounded-sm border-l-4 border-t-4 border-white/50"></div>
              <div className="absolute right-0 top-0 h-12 w-12 rounded-sm border-r-4 border-t-4 border-white/50"></div>
              <div className="absolute bottom-0 left-0 h-12 w-12 rounded-sm border-b-4 border-l-4 border-white/50"></div>
              <div className="absolute bottom-0 right-0 h-12 w-12 rounded-sm border-b-4 border-r-4 border-white/50"></div>

              {/* Center scan icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Scan className="h-16 w-16  text-white/50" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {isModalOpen && (
        <ModalOverlay
          isDismissable={!isVerifying}
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-sm"
        >
          <AriaModal className="w-4/5 max-w-md rounded-3xl bg-medium-gray p-8 shadow-2xl">
            {isVerifying || isRedeeming ? (
              <div className="flex flex-col items-center gap-6">
                <div className="relative h-20 w-20">
                  <div className="absolute inset-0 animate-spin rounded-full border-4 border-light-gray border-t-primary"></div>
                  <div className="absolute inset-2 rounded-full bg-dark-gray"></div>
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold text-foreground">
                    {isRedeeming ? t("coupon.redeeming") : t("coupon.verifying")}
                  </p>
                  <p className="mt-2 text-sm text-brand-gray">
                    {isRedeeming
                      ? t("coupon.scan.redeeming")
                      : t("coupon.scan.verifying")}
                  </p>
                </div>
              </div>
            ) : redeemed ? (
              <div className="flex flex-col items-center gap-6">
                {/* Redeemed State */}
                <div className="relative">
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-500/20">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500">
                      <Check className="h-12 w-12 text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-foreground">
                    {t("coupon.scan.redeemed")}
                  </h3>
                  <p className="mt-2 text-sm text-brand-gray">
                    {t("coupon.scan.redeemedMessage")}
                  </p>
                </div>

                <button
                  className="mt-2 w-full rounded-xl bg-primary py-3.5 font-semibold text-white transition-all hover:bg-primary/90 active:scale-95"
                  onClick={closeModal}
                >
                  {t("coupon.close")}
                </button>
              </div>
            ) : verificationResult ? (
              <div className="flex flex-col items-center gap-6">
                {verificationResult.isValid ? (
                  <>
                    {/* Success State */}
                    <div className="relative">
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green-500/20">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500">
                          <Check className="h-12 w-12 text-white" strokeWidth={3} />
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-foreground">
                        {t("coupon.valid")}
                      </h3>
                      <p className="mt-2 text-sm text-brand-gray">
                        {t("coupon.scan.validMessage")}
                      </p>
                    </div>

                    {verificationResult.coupon && (
                      <div className="w-full space-y-3 rounded-2xl bg-dark-gray p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-brand-gray">
                            {t("coupon.details.expires")}:
                          </span>
                          <span className="text-sm font-medium text-foreground">
                            {new Date(
                              verificationResult.coupon.expiresAt,
                            ).toLocaleDateString(getDateLocale(), {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                      </div>
                    )}

                    <button
                      className="mt-2 w-full rounded-xl bg-green-500 py-3.5 font-semibold text-white transition-all hover:bg-green-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleRedeemCoupon}
                      disabled={isRedeeming}
                    >
                      {t("coupon.scan.confirmButton")}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Error State */}
                    <div className="relative">
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary/20">
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary">
                          <X className="h-12 w-12 text-white" strokeWidth={3} />
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-foreground">
                        {t("coupon.invalid")}
                      </h3>
                      {verificationResult.reason && (
                        <p className="mt-2 text-sm text-brand-gray">
                          {verificationResult.reason}
                        </p>
                      )}
                    </div>

                    <button
                      className="mt-2 w-full rounded-xl bg-primary py-3.5 font-semibold text-white transition-all hover:bg-primary/90 active:scale-95"
                      onClick={closeModal}
                    >
                      {t("coupon.close")}
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </AriaModal>
        </ModalOverlay>
      )}
    </div>
  );
}

export default ScanQRCode;
